import { useTranslation } from 'react-i18next';
import { CreateAndUpdateMandantCredsModal } from '../../../../modals/CreateAndUpdateMandantCredsModal';
import React, { Fragment, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  DEFAULT_TEXT_HEIGHT,
  Divider,
  FormRow,
  IconButton,
  Skeleton,
  Spacer,
  Stack,
  Tabs,
  Text,
  Title,
  Wizard
} from '@efast_public/fjd-component-library';
import { CreateAndUpdateFitCoDestinationModal } from '../../../../modals/CreateAndUpdateFitCoDestinationModal';
import { useNotification } from '../../../../shared/notification/useNotification';
import { useOrganizationForwardingDelete } from '../../../../api/hooks/useOrganizationForwarding';
import {
  ForwardingConfig,
  ForwardingDestinationDto,
  ForwardingPublicService
} from '../../../../api/models/Forwarding/Forwarding';
import Summary from '../../../../modals/ReceiverSettingsWizard/Summary/Summary';
import StartInfo from '../../../../modals/ReceiverSettingsWizard/Startinfo/StartInfo';
import { RenameOrgUnitModal } from '../../../../modals/RenameOrgUnitModal';
import { DetailPublicServiceModal } from '../../../../modals/DetailPublicServiceModal';
import { OrganizationDto } from '../../../../api/models/Organizations/Organization';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useAdminTasks,
  useDeleteAdminTask,
  CHECK_DESTINATION_SIGNING_KEY_PAIR_TASK_CODE_PREFIX,
  CHECK_DESTINATION_ENCRYPTION_KEY_PAIR_TASK_CODE_PREFIX
} from '../../../../api/hooks/useAdminTasks';
import SubmissionTasksTab from './SubmissionTasksTab';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useDestinationsLastSync } from '../../../../api/hooks/useDestinationsLastSync';
import DestinationRenaming from '../../../ConfigAssistant/ConfigAssistantSteps/Destinations/DestinationRenaming';
import DataModel from '../Datamodels/DataModel';
import { FitConnect } from '../../../ConfigAssistant/ConfigAssistantSteps/Fitconnect/FitConnect';
import DestinationKeyUpload from '../../../ConfigAssistant/ConfigAssistantSteps/Destinations/DestinationKeyUpload';

interface Service {
  name: string;
  identifier: string;
  createdOn: string;
  active: boolean;
  forwardings?: Array<ForwardingDestinationDto>;
}

interface OrganizationDestination {
  orgUnitId: string;
  orgUnitName: string;
  destinationId: string;
  active: boolean;
  services?: Array<Service>;
}

export default function FitConnectSettings() {
  const { notifyUserFromRequest } = useNotification();
  const { t } = useTranslation();
  const { confirmByUser } = useNotification();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteForwarding } = useOrganizationForwardingDelete();
  const [visibleModal, setVisibleModal] = useState<
    null | 'creds' | 'fitcoWizard'
  >(null);
  const [organizationForwarding, setOrganizationForwarding] = useState<
    Array<OrganizationDestination>
  >([]);
  const [publicServiceOfOrgUnit, setPublicServiceOfOrgUnit] = useState<
    | ({
        orgUnitId: string;
        publicServiceId: string;
        orgIndex: number;
        serviceIndex: number;
        name?: string;
        modalType: 'info' | 'createOrUpdate';
      } & Partial<ForwardingConfig>)
    | null
  >(null);
  const [orgUnit, setOrgUnit] = useState<{
    id: string;
    name: string;
    destinationId: string;
  } | null>(null);

  const fetchOrganizationForwardingPerPublicService = (
    orgUnitId: string,
    publicServiceIdentifier: string
  ) =>
    queryClient.fetchQuery<Array<ForwardingDestinationDto>>({
      queryKey: [
        BehApiRoutes.fowardingsByPublicServicesByIdAndByOrgUnit(
          orgUnitId,
          publicServiceIdentifier
        )
      ]
    });
  const { data: adminTasks } = useAdminTasks();
  const { mutate: deleteAdminTask } = useDeleteAdminTask();

  const signingKeyMismatchTasks = new Map(
    (adminTasks ?? [])
      .filter(
        (task) =>
          task.taskType.startsWith(
            CHECK_DESTINATION_SIGNING_KEY_PAIR_TASK_CODE_PREFIX
          ) && !!task.taskSpecificInfo?.destinationId
      )
      .map((task) => [task.taskSpecificInfo!.destinationId, task.id])
  );

  const signingKeyMismatchDestinationIds = new Set(
    signingKeyMismatchTasks.keys()
  );

  const encryptionKeyMismatchTasks = new Map(
    (adminTasks ?? [])
      .filter(
        (task) =>
          task.taskType.startsWith(
            CHECK_DESTINATION_ENCRYPTION_KEY_PAIR_TASK_CODE_PREFIX
          ) && !!task.taskSpecificInfo?.destinationId
      )
      .map((task) => [task.taskSpecificInfo!.destinationId, task.id])
  );

  const encryptionKeyMismatchDestinationIds = new Set(
    encryptionKeyMismatchTasks.keys()
  );

  const { refetch: fetchAllOrganizations, isLoading } = useQuery({
    queryKey: [BehApiRoutes.orgUnits()],
    queryFn: () =>
      notifyUserFromRequest<Array<OrganizationDto>, unknown>(() =>
        behClient.get(BehApiRoutes.orgUnits())
      ).then((responseOrgs) => {
        return Promise.all(
          responseOrgs.map((org) =>
            queryClient.fetchQuery<Array<ForwardingPublicService>>({
              queryKey: [BehApiRoutes.publicServicesByOrgUnit(org.id)]
            })
          )
        ).then((publicServicesResponse) => {
          const organizationForwardingData =
            responseOrgs.map<OrganizationDestination>((org, index) => ({
              destinationId: org.destinationId,
              orgUnitId: org.id,
              orgUnitName: org.name,
              active: org.active,
              services: publicServicesResponse[index]
            }));

          setOrganizationForwarding(organizationForwardingData);
        });
      })
  });

  const { data: lastSyncedAt, refetch } = useDestinationsLastSync();

  const updateServiceAndUpdateUi = (
    orgUnitId: string,
    publicServiceIdentifier: string,
    serviceIndex: number,
    orgIndex: number
  ) => {
    fetchOrganizationForwardingPerPublicService(
      orgUnitId,
      publicServiceIdentifier
    ).then((result) => {
      const updatedOrgs = [...organizationForwarding];
      // @ts-expect-error deep nested property assignment
      updatedOrgs[orgIndex].services[serviceIndex].forwardings = result;
      setOrganizationForwarding(updatedOrgs);
    });
  };

  return (
    <Stack size="xl">
      <Tabs>
        <Tabs.List>
          <Tabs.Tab label={t('weiterleitung.tabs.forwarding')} />
          <Tabs.Tab
            label={t('weiterleitung.tabs.scheduledTasks')}
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: [BehApiRoutes.scheduledTasks()],
                exact: false
              })
            }
          />
        </Tabs.List>
        <Tabs.Panels>
          <Tabs.Panel>
            <div style={{ width: '50rem' }}>
              <Stack spacing={'l'}>
                <Stack direction="column" spacing="s">
                  <Alert
                    message={t('settings.weiterleitung.updateSenderCreds.info')}
                    variant="info"
                  >
                    <Button
                      size="sm"
                      onClick={() => {
                        setVisibleModal('creds');
                      }}
                    >
                      {t('settings.weiterleitung.createMandantCreds')}
                    </Button>
                  </Alert>
                  <Alert
                    message={t(
                      'settings.weiterleitung.updateReceiverCreds.warning',
                      {
                        timestamp: lastSyncedAt
                          ? new Date(lastSyncedAt).toLocaleString()
                          : '--:--'
                      }
                    )}
                    variant="warning"
                  >
                    <Button
                      size="sm"
                      onClick={() => {
                        setVisibleModal('fitcoWizard');
                      }}
                    >
                      {t('settings.weiterleitung.updateReceiverCreds.button')}
                    </Button>
                  </Alert>
                </Stack>
                <Spacer />
                <Title level="3" visualLevel={6}>
                  Zustellpunkte
                </Title>
                <Stack spacing="3xl">
                  {organizationForwarding &&
                    organizationForwarding
                      .filter((org) => org.active)
                      .map((org) => ({
                        org,
                        orgIndex: organizationForwarding.findIndex(
                          (o) => o.orgUnitId === org.orgUnitId
                        )
                      }))
                      .map(({ org, orgIndex }) => (
                        <Stack spacing="xs" key={org.orgUnitId}>
                          <Stack
                            spacing="s"
                            direction="row"
                            justifyContent="space-between"
                          >
                            <Stack>
                              <Text size="m" strong>
                                {org.orgUnitName}
                              </Text>
                              <Text size="xs">
                                {t(
                                  'settings.weiterleitung.serviceDestinationId.description',
                                  { destinationId: org.destinationId }
                                )}
                              </Text>
                            </Stack>
                            <Button
                              size="sm"
                              variant="primary-text"
                              beforeIcon="edit"
                              onClick={() => {
                                setOrgUnit({
                                  id: org.orgUnitId,
                                  name: org.orgUnitName,
                                  destinationId: org.destinationId
                                });
                              }}
                            >
                              {t('settings.weiterleitung.renameOrgUnit.button')}
                            </Button>
                          </Stack>
                          {signingKeyMismatchDestinationIds.has(
                            org.destinationId
                          ) && (
                            <Alert
                              variant="error"
                              message={t(
                                'weiterleitung.signingKeyMismatch.alert'
                              )}
                              onClose={() => {
                                const taskId = signingKeyMismatchTasks.get(
                                  org.destinationId
                                );
                                if (taskId !== undefined) {
                                  deleteAdminTask(taskId);
                                }
                              }}
                            />
                          )}
                          {encryptionKeyMismatchDestinationIds.has(
                            org.destinationId
                          ) && (
                            <Alert
                              variant="error"
                              message={t(
                                'weiterleitung.encryptionKeyMismatch.alert'
                              )}
                              onClose={() => {
                                const taskId = encryptionKeyMismatchTasks.get(
                                  org.destinationId
                                );
                                if (taskId !== undefined) {
                                  deleteAdminTask(taskId);
                                }
                              }}
                            />
                          )}
                          <Stack spacing="m">
                            {org.services &&
                              org.services.map((service, serviceIndex) => (
                                <Accordion
                                  key={org.orgUnitId + service.identifier}
                                  label={service.name ?? service.identifier}
                                  seperateExpandButton
                                  onToggle={() => {
                                    const serviceForwardings =
                                      organizationForwarding
                                        .at(orgIndex)
                                        ?.services?.at(
                                          serviceIndex
                                        )?.forwardings;
                                    if (!serviceForwardings) {
                                      updateServiceAndUpdateUi(
                                        org.orgUnitId,
                                        service.identifier,
                                        serviceIndex,
                                        orgIndex
                                      );
                                    }
                                  }}
                                  after={
                                    <Stack
                                      spacing="xs"
                                      direction="row"
                                      alignItems="center"
                                    >
                                      {!service.active && (
                                        <Badge size="sm" variant="neutral">
                                          {t(
                                            'settings.weiterleitung.serviceInactive.badge'
                                          )}
                                        </Badge>
                                      )}
                                      <Button
                                        beforeIcon="information-outline"
                                        size="sm"
                                        variant="primary-text"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          setPublicServiceOfOrgUnit({
                                            orgUnitId: org.orgUnitId,
                                            publicServiceId: service.identifier,
                                            name: service.name,
                                            serviceIndex,
                                            orgIndex,
                                            modalType: 'info'
                                          });
                                        }}
                                      >
                                        {t('settings.weiterleitung.info')}
                                      </Button>
                                      <Button
                                        beforeIcon="add-outline"
                                        size="sm"
                                        variant="primary-text"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          setPublicServiceOfOrgUnit({
                                            orgUnitId: org.orgUnitId,
                                            publicServiceId: service.identifier,
                                            serviceIndex,
                                            orgIndex,
                                            modalType: 'createOrUpdate'
                                          });
                                        }}
                                      >
                                        {t(
                                          'settings.weiterleitung.addDestionationId.button'
                                        )}
                                      </Button>
                                    </Stack>
                                  }
                                >
                                  <Stack>
                                    {(service?.forwardings ?? []).map(
                                      (forwarding, forwardingIndex) => (
                                        <Fragment
                                          key={`${forwarding.name}-${forwardingIndex}`}
                                        >
                                          <Divider />
                                          <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                          >
                                            <FormRow
                                              label={forwarding.name}
                                              description={t(
                                                'settings.weiterleitung.serviceDestinationId.description',
                                                {
                                                  destinationId:
                                                    forwarding
                                                      .forwardingTargetConfig
                                                      .destinationId
                                                }
                                              )}
                                            >
                                              <div />
                                            </FormRow>
                                            <Stack direction="row" spacing="s">
                                              <IconButton
                                                icon={'edit'}
                                                size="md"
                                                variant="secondary-outline"
                                                label={t(
                                                  'settings.weiterleitung.edit'
                                                )}
                                                onClick={() => {
                                                  setPublicServiceOfOrgUnit({
                                                    orgUnitId: org.orgUnitId,
                                                    publicServiceId:
                                                      service.identifier,
                                                    name: forwarding.name ?? '',
                                                    orgIndex,
                                                    serviceIndex,
                                                    modalType: 'createOrUpdate',
                                                    ...forwarding.forwardingTargetConfig
                                                  });
                                                }}
                                              />
                                              <IconButton
                                                icon={'trashcan'}
                                                size="md"
                                                variant="secondary-outline"
                                                label={t(
                                                  'settings.weiterleitung.delete'
                                                )}
                                                onClick={() => {
                                                  confirmByUser({
                                                    callback: () => {
                                                      deleteForwarding({
                                                        orgUnitId:
                                                          org.orgUnitId,
                                                        forwardingType:
                                                          'FITCONNECT',
                                                        fromPublicServiceIdentifier:
                                                          service.identifier,
                                                        ...forwarding.forwardingTargetConfig
                                                      })
                                                        .then(() => {
                                                          updateServiceAndUpdateUi(
                                                            org.orgUnitId,
                                                            service.identifier,
                                                            serviceIndex,
                                                            orgIndex
                                                          );
                                                        })
                                                        .catch(() => {});
                                                    },
                                                    title: t(
                                                      'settings.weiterleitung.confirmDelete.title'
                                                    ),
                                                    content: t(
                                                      'settings.weiterleitung.confirmDelete.content',
                                                      { name: forwarding.name }
                                                    )
                                                  });
                                                }}
                                              />
                                            </Stack>
                                          </Stack>
                                        </Fragment>
                                      )
                                    )}
                                  </Stack>
                                </Accordion>
                              ))}
                          </Stack>
                        </Stack>
                      ))}
                </Stack>
                {organizationForwarding.some((org) => !org.active) && (
                  <Stack spacing="xs">
                    <Text size="m" strong>
                      {t('settings.weiterleitung.inactiveOrgUnits.heading')}
                    </Text>
                    {organizationForwarding
                      .filter((org) => !org.active)
                      .map((org) => ({
                        org,
                        orgIndex: organizationForwarding.findIndex(
                          (o) => o.orgUnitId === org.orgUnitId
                        )
                      }))
                      .map(({ org, orgIndex }) => (
                        <Stack spacing="xs" key={org.orgUnitId}>
                          <Stack
                            spacing="s"
                            direction="row"
                            justifyContent="space-between"
                          >
                            <Stack>
                              <Text size="m" strong>
                                {org.orgUnitName}
                              </Text>
                              <Text size="xs">
                                {t(
                                  'settings.weiterleitung.serviceDestinationId.description',
                                  { destinationId: org.destinationId }
                                )}
                              </Text>
                            </Stack>
                            <Button
                              size="sm"
                              variant="primary-text"
                              beforeIcon="edit"
                              onClick={() => {
                                setOrgUnit({
                                  id: org.orgUnitId,
                                  name: org.orgUnitName,
                                  destinationId: org.destinationId
                                });
                              }}
                            >
                              {t('settings.weiterleitung.renameOrgUnit.button')}
                            </Button>
                          </Stack>
                          <Stack spacing="m">
                            {org.services &&
                              org.services.map((service, serviceIndex) => (
                                <Accordion
                                  key={org.orgUnitId + service.identifier}
                                  label={service.name ?? service.identifier}
                                  seperateExpandButton
                                  onToggle={() => {
                                    const serviceForwardings =
                                      organizationForwarding
                                        .at(orgIndex)
                                        ?.services?.at(
                                          serviceIndex
                                        )?.forwardings;
                                    if (!serviceForwardings) {
                                      updateServiceAndUpdateUi(
                                        org.orgUnitId,
                                        service.identifier,
                                        serviceIndex,
                                        orgIndex
                                      );
                                    }
                                  }}
                                  after={
                                    <Stack
                                      spacing="xs"
                                      direction="row"
                                      alignItems="center"
                                    >
                                      {!service.active && (
                                        <Badge size="sm" variant="neutral">
                                          {t(
                                            'settings.weiterleitung.serviceInactive.badge'
                                          )}
                                        </Badge>
                                      )}
                                      <Button
                                        beforeIcon="information-outline"
                                        size="sm"
                                        variant="primary-text"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          setPublicServiceOfOrgUnit({
                                            orgUnitId: org.orgUnitId,
                                            publicServiceId: service.identifier,
                                            name: service.name,
                                            serviceIndex,
                                            orgIndex,
                                            modalType: 'info'
                                          });
                                        }}
                                      >
                                        {t('settings.weiterleitung.info')}
                                      </Button>
                                      <Button
                                        beforeIcon="add-outline"
                                        size="sm"
                                        variant="primary-text"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          setPublicServiceOfOrgUnit({
                                            orgUnitId: org.orgUnitId,
                                            publicServiceId: service.identifier,
                                            serviceIndex,
                                            orgIndex,
                                            modalType: 'createOrUpdate'
                                          });
                                        }}
                                      >
                                        {t(
                                          'settings.weiterleitung.addDestionationId.button'
                                        )}
                                      </Button>
                                    </Stack>
                                  }
                                >
                                  <Stack>
                                    {(service?.forwardings ?? []).map(
                                      (forwarding, forwardingIndex) => (
                                        <Fragment
                                          key={`${forwarding.name}-${forwardingIndex}`}
                                        >
                                          <Divider />
                                          <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                          >
                                            <FormRow
                                              label={forwarding.name}
                                              description={t(
                                                'settings.weiterleitung.serviceDestinationId.description',
                                                {
                                                  destinationId:
                                                    forwarding
                                                      .forwardingTargetConfig
                                                      .destinationId
                                                }
                                              )}
                                            >
                                              <div />
                                            </FormRow>
                                            <Stack direction="row" spacing="s">
                                              <IconButton
                                                icon={'edit'}
                                                size="md"
                                                variant="secondary-outline"
                                                label={t(
                                                  'settings.weiterleitung.edit'
                                                )}
                                                onClick={() => {
                                                  setPublicServiceOfOrgUnit({
                                                    orgUnitId: org.orgUnitId,
                                                    publicServiceId:
                                                      service.identifier,
                                                    name: forwarding.name ?? '',
                                                    orgIndex,
                                                    serviceIndex,
                                                    modalType: 'createOrUpdate',
                                                    ...forwarding.forwardingTargetConfig
                                                  });
                                                }}
                                              />
                                              <IconButton
                                                icon={'trashcan'}
                                                size="md"
                                                variant="secondary-outline"
                                                label={t(
                                                  'settings.weiterleitung.delete'
                                                )}
                                                onClick={() => {
                                                  confirmByUser({
                                                    callback: () => {
                                                      deleteForwarding({
                                                        orgUnitId:
                                                          org.orgUnitId,
                                                        forwardingType:
                                                          'FITCONNECT',
                                                        fromPublicServiceIdentifier:
                                                          service.identifier,
                                                        ...forwarding.forwardingTargetConfig
                                                      })
                                                        .then(() => {
                                                          updateServiceAndUpdateUi(
                                                            org.orgUnitId,
                                                            service.identifier,
                                                            serviceIndex,
                                                            orgIndex
                                                          );
                                                        })
                                                        .catch(() => {});
                                                    },
                                                    title: t(
                                                      'settings.weiterleitung.confirmDelete.title'
                                                    ),
                                                    content: t(
                                                      'settings.weiterleitung.confirmDelete.content',
                                                      { name: forwarding.name }
                                                    )
                                                  });
                                                }}
                                              />
                                            </Stack>
                                          </Stack>
                                        </Fragment>
                                      )
                                    )}
                                  </Stack>
                                </Accordion>
                              ))}
                          </Stack>
                        </Stack>
                      ))}
                  </Stack>
                )}
                {isLoading && (
                  <Stack spacing="s">
                    <Skeleton height={'20px'} width={'200px'} />
                    <Skeleton width={'300px'} />
                    {['a', 'b', 'c', 'd'].map((entry) => (
                      <Accordion
                        key={entry}
                        label={
                          <div
                            style={{
                              height: DEFAULT_TEXT_HEIGHT,
                              width: '200px'
                            }}
                          >
                            <Skeleton height={DEFAULT_TEXT_HEIGHT} />
                          </div>
                        }
                      >
                        <div />
                      </Accordion>
                    ))}
                  </Stack>
                )}
              </Stack>
            </div>
          </Tabs.Panel>
          <Tabs.Panel>
            <SubmissionTasksTab />
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs>
      {visibleModal === 'creds' && (
        <CreateAndUpdateMandantCredsModal
          onClose={() => {
            setVisibleModal(null);
          }}
          isOpen
        />
      )}
      {publicServiceOfOrgUnit?.modalType === 'createOrUpdate' && (
        <CreateAndUpdateFitCoDestinationModal
          onClose={() => {
            setPublicServiceOfOrgUnit(null);
          }}
          onSuccess={() => {
            setPublicServiceOfOrgUnit(null);
            updateServiceAndUpdateUi(
              publicServiceOfOrgUnit.orgUnitId,
              publicServiceOfOrgUnit.publicServiceId,
              publicServiceOfOrgUnit.serviceIndex,
              publicServiceOfOrgUnit.orgIndex
            );
          }}
          isOpen
          orgUnitId={publicServiceOfOrgUnit.orgUnitId}
          publicServiceId={publicServiceOfOrgUnit.publicServiceId}
          publicServiceIdentifier={
            publicServiceOfOrgUnit.publicServiceIdentifier
          }
          destinationId={publicServiceOfOrgUnit.destinationId}
          forwardingName={publicServiceOfOrgUnit.name}
        />
      )}
      {visibleModal === 'fitcoWizard' && (
        <Wizard
          size="l"
          abortButtonLabel="Abbrechen"
          confirmButtonLabel="Abschließen"
          defaultHeading="bla"
          defaultPageUpButtonLabel="Weiter"
          defaultPageDownButtonLabel="Zurück"
          onClose={() => {
            setVisibleModal(null);
            refetch().catch(console.error);
          }}
          startOnPageIndex={0}
          onConfirmed={() => {
            fetchAllOrganizations().catch(() => {});
            setVisibleModal(null);
            refetch().catch(console.error);
          }}
          closeOnBackdropClick
          isOpen
        >
          <StartInfo key="startInfo" />
          <FitConnect isSettings key="receiver-config" />
          <DestinationRenaming isSettings key="destination-renaming" />
          <DestinationKeyUpload isSettings key="destination-security" />
          <DataModel isSettings key="synched-data-models" />
          <Summary key="summary" />
        </Wizard>
      )}
      {orgUnit && (
        <RenameOrgUnitModal
          orgUnitId={orgUnit.id}
          defaultOrgUnitName={orgUnit.name}
          onClose={() => {
            setOrgUnit(null);
          }}
          onSuccess={() => {
            fetchAllOrganizations().catch(() => {});
            setOrgUnit(null);
          }}
        />
      )}
      {publicServiceOfOrgUnit?.modalType === 'info' && (
        <DetailPublicServiceModal
          key={publicServiceOfOrgUnit.orgUnitId}
          onClose={() => {
            setPublicServiceOfOrgUnit(null);
          }}
          publicServiceIdentifier={publicServiceOfOrgUnit.publicServiceId}
          publicServiceName={publicServiceOfOrgUnit.name!}
          orgUnitId={publicServiceOfOrgUnit.orgUnitId}
        />
      )}
    </Stack>
  );
}
